/**
 * AppFrameRoute — a rota de layout de TODO o app emoldurado (06/10/2026).
 *
 * ## A casca das rotas de livro é decidida pela SESSÃO (05/09)
 *
 * `/livro/:workSlug` é a única rota do app que serve dois públicos ao mesmo
 * tempo: quem tem conta (e quer o app, com a nav inferior, o Foco e o frame de
 * 430px no desktop) e quem chegou pelo Google sem conta (e precisa de um SITE:
 * cabeçalho com marca e "Criar conta", largura plena, rodapé). Até 05/09 os
 * dois recebiam o `AppFrame`, e no desktop o visitante via a ficha dentro de
 * uma moldura de celular no meio de um fundo cinza — o app com as portas
 * fechadas, não uma página.
 *
 * Nas rotas de livro (`BOOK_ROUTE_PATHS`), a regra é a MESMA do
 * `PublicOrGuarded`: sem sessão e com o dado servido pela `renderBookPage` =
 * versão site; qualquer outro caso = o `AppFrame` de sempre, com todos os
 * watchers e o host de overlays que as páginas do app esperam. A rota é lida
 * do endereço, e não de uma prop por grupo, porque o livro e o resto do app
 * são UM grupo só de rotas: é o universo que o `KeepAliveOutlet` guarda.
 *
 * ⚠️ Enquanto a sessão carrega, sem usuário e com payload, a casca é a do
 * site: a PublicOrGuarded mostra o loader nesse intervalo, e a alternativa
 * (frame de celular → site) faria a moldura piscar no desktop justamente para
 * o público desta rota. Se um usuário se materializar, a casca vira o frame; no
 * mobile as duas são visualmente idênticas.
 *
 * ## Por que o livro e o resto do app usam o MESMO componente (BUG-331)
 *
 * O React Router não põe `key` nas rotas de layout: o que decide se a casca
 * sobrevive à navegação é o TIPO do elemento na mesma posição. Quando o livro
 * tinha um componente próprio (`BookRouteFrame`) e o resto do app usava
 * `<AppFrame />` direto, Início → livro → voltar trocava um tipo pelo outro e
 * o `AppFrame` era desmontado e montado de novo nas duas direções — scroller,
 * BottomNav, watchers e host do Foco nasciam do zero, e a tela piscava no
 * voltar (medido em harness: o `#app-scroll-root` era outro elemento 3/3).
 * Hoje é um grupo só (o livro mora junto do resto); rota nova que deva viver
 * dentro do frame entra NESTE grupo, nunca num `<AppFrame />` cru ao lado.
 */
import { Outlet, matchPath, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { AppFrame } from "../layouts/AppFrame";
import { hasPublicPayload } from "./publicPayload";

/** Rotas de livro: visitante sem conta com payload recebe a versão site. */
export const BOOK_ROUTE_PATHS = ["/book/:id", "/livro/:workSlug"] as const;

export function AppFrameRoute() {
  const { user, isAnonymous } = useAuth();
  const { pathname } = useLocation();
  const bookRoute = BOOK_ROUTE_PATHS.some((p) => matchPath(p, pathname) !== null);
  if (bookRoute && (!user || isAnonymous) && hasPublicPayload("book")) {
    return (
      <div className="min-h-dvh bg-background font-body">
        <Outlet />
      </div>
    );
  }
  return <AppFrame />;
}
